import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Trash2, Plus, Minus } from 'lucide-react';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal } = useCart();

  // Debug logging
  console.log('[Cart Page] Cart items:', cartItems);
  console.log('[Cart Page] Cart items length:', cartItems?.length);

  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <h2 className="text-3xl font-medium text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Add some products to get started!</p>
          <Link to="/edible-greetings/catalog">
            <Button data-testid="continue-shopping-btn">Continue Shopping</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Safely compute cart total
  const safeTotal = () => {
    try {
      return getCartTotal().toFixed(2);
    } catch (e) {
      console.error('[Cart] Error computing total:', e);
      return '0.00';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-medium text-gray-900 mb-8">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item, index) => {
              // Safe price access
              const itemPrice = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
              const itemQuantity = item.quantity || 1;
              
              // Safely handle image - limit base64 to reasonable size for display
              const getDisplayImage = () => {
                if (!item.image) return '/placeholder.png';
                // If it's a base64 image, use it directly
                if (item.image.startsWith('data:')) return item.image;
                // If it's a URL, use it
                return item.image;
              };
              
              console.log('[Cart] Rendering item:', { 
                id: item.id, 
                name: item.name, 
                price: itemPrice,
                hasImage: !!item.image,
                imageType: item.image?.substring(0, 30)
              });
              
              return (
                <Card key={`${item.id}-${index}`} className="p-6" data-testid={`cart-item-${index}`}>
                  <div className="flex gap-6">
                    {/* Image */}
                    <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                      <img
                        src={getDisplayImage()}
                        alt={item.name || 'Product'}
                        className="w-full h-full object-cover"
                        onError={(e) => { 
                          console.log('[Cart] Image load error');
                          e.target.src = '/placeholder.png'; 
                        }}
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900 mb-1">{item.name || 'Product'}</h3>
                          <p className="text-gray-600 text-sm mb-2">${itemPrice.toFixed(2)}</p>
                          {item.customization?.message && (
                            <p className="text-sm text-gray-600 italic mb-2">
                              Message: "{item.customization.message}"
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id, item.customization)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>

                      {/* Quantity controls */}
                      <div className="flex items-center space-x-3 mt-4">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, itemQuantity - 1, item.customization)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="font-medium w-8 text-center">{itemQuantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateQuantity(item.id, itemQuantity + 1, item.customization)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <span className="ml-auto font-medium text-gray-900">
                          ${(itemPrice * itemQuantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24">
              <h2 className="text-xl font-medium text-gray-900 mb-6">Order Summary</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${safeTotal()}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="border-t border-gray-200 pt-3 flex justify-between font-medium text-gray-900 text-lg">
                  <span>Total</span>
                  <span>${safeTotal()}</span>
                </div>
              </div>

              <Button
                className="w-full py-6 text-lg mb-4"
                onClick={() => navigate('/edible-greetings/checkout')}
              >
                Proceed to Checkout
              </Button>

              <Link to="/edible-greetings/catalog">
                <Button variant="outline" className="w-full">
                  Continue Shopping
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
